import React from 'react';
import PropTypes from 'prop-types';

import { Box, Typography } from '@mui/material';

import StatusBadge, {
  STATUS_BADGE_VARIANTS,
} from '../../../../../../../components/EmployeeDocStatusBadge';
import { BLACK, PRIMARY_PURPLE } from '../../../../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../../../../theme/fontConstants';
import {
  epochToDateInReadableFormat,
  epochToTimeInReadableFormat,
} from '../../../../../../../utils';

import DocumentActionsMenu, { DOCUMENT_ACTIONS_MENU_VARIANTS } from './DocumentActionsMenu';

const cardRootSx = {
  backgroundColor: BLACK[0],
  px: 2,
  py: 1.25,
  transition: 'background-color 300ms ease',
  '&:hover': {
    backgroundColor: PRIMARY_PURPLE[10],
  },
  '&:hover .document-card-title': {
    fontFamily: PRIMARY_FONT[700],
  },
};

const titleSx = {
  fontFamily: PRIMARY_FONT[400],
  fontSize: '15px',
  lineHeight: '16px',
  letterSpacing: '-0.01em',
  color: BLACK[100],
  transition: 'font-family 300ms ease',
};

const secondaryTextSx = {
  fontFamily: PRIMARY_FONT[400],
  fontSize: '11px',
  lineHeight: '100%',
  letterSpacing: '0%',
  color: BLACK[60],
};

const DocumentCard = ({
  title,
  statusLabel,
  statusText,
  statusVariant,
  submittedAtEpoch,
  isActionLoading,
  document,
  onView,
  onUploadHistory,
  onSelectFormStatus,
  onSelectFormValidity,
  onSelectSetExpiration,
}) => {
  const formattedDate = epochToDateInReadableFormat(submittedAtEpoch);
  const formattedTime = epochToTimeInReadableFormat(submittedAtEpoch);

  return (
    <Box sx={cardRootSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Typography className="document-card-title" sx={titleSx}>
          {title}
        </Typography>
        <DocumentActionsMenu
          document={document}
          isActionLoading={isActionLoading}
          onSelectFormStatus={onSelectFormStatus}
          onSelectFormValidity={onSelectFormValidity}
          onSelectSetExpiration={onSelectSetExpiration}
          onUploadHistory={onUploadHistory}
          onView={onView}
          variant={DOCUMENT_ACTIONS_MENU_VARIANTS.CARD}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mt: 0.5 }}>
        <StatusBadge label={statusLabel} variant={statusVariant} />
        <Typography sx={secondaryTextSx}>{statusText}</Typography>
        <Typography sx={secondaryTextSx}>
          {formattedDate}
          {formattedDate && formattedTime ? ', ' : ''}
          {formattedTime}
        </Typography>
      </Box>
    </Box>
  );
};

DocumentCard.propTypes = {
  document: PropTypes.shape({
    _id: PropTypes.string,
    state: PropTypes.shape({ status: PropTypes.string }),
    validity: PropTypes.shape({
      status: PropTypes.string,
      range: PropTypes.shape({
        validFrom: PropTypes.string,
        validNoticeFrom: PropTypes.string,
        validThrough: PropTypes.string,
      }),
    }),
  }).isRequired,
  isActionLoading: PropTypes.bool,
  onSelectFormStatus: PropTypes.func,
  onSelectFormValidity: PropTypes.func,
  onSelectSetExpiration: PropTypes.func,
  onUploadHistory: PropTypes.func,
  onView: PropTypes.func,
  statusLabel: PropTypes.string.isRequired,
  statusText: PropTypes.string.isRequired,
  statusVariant: PropTypes.oneOf(Object.values(STATUS_BADGE_VARIANTS)).isRequired,
  submittedAtEpoch: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
};

DocumentCard.defaultProps = {
  isActionLoading: false,
  onSelectFormStatus: () => {},
  onSelectFormValidity: () => {},
  onSelectSetExpiration: () => {},
  onUploadHistory: () => {},
  onView: () => {},
};

export default DocumentCard;
